// src/pages/Profile.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getAuth,
  updatePassword as fbUpdatePassword,
  verifyBeforeUpdateEmail,
} from 'firebase/auth';
import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../contexts/firebase/config';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';

import {
  Paper,
  Box,
  Avatar,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Stack,
  Chip,
  Collapse,
  Divider,
  alpha,
  useTheme,
} from '@mui/material';

import PaletteRoundedIcon from '@mui/icons-material/PaletteRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import SettingsBrightnessRoundedIcon from '@mui/icons-material/SettingsBrightnessRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';

import sintanetLogo from '../assets/sintanet-logo.png';

function getSystemThemeMode() {
  if (
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  ) {
    return 'dark';
  }
  return 'light';
}

export default function Profile() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const { currentUser, userType } = useAuth();
  const auth = getAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [fields, setFields] = useState({
    nome: '',
    morada: '',
    nif: '',
    contacto: '',
    email: currentUser?.email || '',
  });

  const [pwdDialogOpen, setPwdDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [showAppearance, setShowAppearance] = useState(false);
  const [appearanceMode, setAppearanceMode] = useState(() => {
    if (typeof window === 'undefined') return 'system';
    return localStorage.getItem('app-theme-mode') || 'system';
  });
  const [systemMode, setSystemMode] = useState(getSystemThemeMode());

  const colors = {
    accent: '#ff6a00',
    success: '#ff8a1f',
    surface: isDark ? 'rgba(15,23,42,0.78)' : 'rgba(255,255,255,0.78)',
    card: isDark ? 'rgba(15,23,42,0.84)' : 'rgba(255,255,255,0.84)',
    cardStrong: isDark ? 'rgba(15,23,42,0.96)' : 'rgba(255,255,255,0.96)',
    border: isDark ? 'rgba(148,163,184,0.16)' : 'rgba(15,23,42,0.08)',
    text: theme.palette.text.primary,
    muted: theme.palette.text.secondary,
    hero: '#080808',
  };

  function showSuccess(message) {
    enqueueSnackbar(message, { variant: 'success' });
  }

  function showError(message) {
    enqueueSnackbar(message, { variant: 'error' });
  }

  function showWarning(message) {
    enqueueSnackbar(message, { variant: 'warning' });
  }

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const media = window.matchMedia('(prefers-color-scheme: dark)');

    const onChange = (e) => {
      setSystemMode(e.matches ? 'dark' : 'light');
    };

    if (media.addEventListener) media.addEventListener('change', onChange);
    else media.addListener(onChange);

    setSystemMode(media.matches ? 'dark' : 'light');

    return () => {
      if (media.removeEventListener) media.removeEventListener('change', onChange);
      else media.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    localStorage.setItem('app-theme-mode', appearanceMode);
    document.documentElement.setAttribute('data-app-theme-mode', appearanceMode);
    window.dispatchEvent(new Event('app-theme-change'));
  }, [appearanceMode]);

  const effectiveThemeMode =
    appearanceMode === 'system' ? systemMode : appearanceMode;

  useEffect(() => {
    async function load() {
      try {
        if (userType === 'partner' && currentUser?.uid) {
          const snap = await getDoc(doc(db, 'empresas', currentUser.uid));

          if (snap.exists()) {
            const d = snap.data();

            setFields((f) => ({
              ...f,
              nome: d.nomeEmpresa || '',
              morada: d.morada || '',
              nif: d.nif || '',
              contacto: d.contacto || '',
              email: currentUser.email || '',
            }));
          } else {
            setFields((f) => ({ ...f, email: currentUser?.email || '' }));
          }
        } else {
          setFields((f) => ({ ...f, email: currentUser?.email || '' }));
        }
      } catch (e) {
        console.error(e);
        showError('Falha ao carregar o perfil.');
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [currentUser, userType]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFields((f) => ({ ...f, [name]: value }));
  };

  const isValidEmail = (val) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(val).toLowerCase());

  const handleSave = async () => {
    setSaving(true);

    try {
      let emailMsg = '';

      if (fields.email && fields.email !== currentUser?.email) {
        if (!isValidEmail(fields.email)) {
          throw new Error('E-mail inválido.');
        }

        await verifyBeforeUpdateEmail(auth.currentUser, fields.email.trim());

        emailMsg =
          'Enviámos um link de confirmação para o novo e-mail. Só depois de clicares nesse link é que o e-mail da conta será atualizado.';
      }

      if (userType === 'partner' && currentUser?.uid) {
        await updateDoc(doc(db, 'empresas', currentUser.uid), {
          nomeEmpresa: fields.nome || null,
          morada: fields.morada || null,
          nif: fields.nif || null,
          contacto: fields.contacto || null,
        });
      }

      showSuccess(emailMsg || 'Perfil atualizado com sucesso.');
    } catch (err) {
      console.error(err);

      const msg =
        err?.code === 'auth/requires-recent-login'
          ? 'Por segurança, entra novamente para atualizar o e-mail.'
          : err?.message || 'Falha ao atualizar perfil.';

      showError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handlePwdChange = async () => {
    if (newPassword.length < 6) {
      showWarning('A palavra-passe deve ter pelo menos 6 caracteres.');
      return;
    }

    try {
      setSaving(true);

      await fbUpdatePassword(auth.currentUser, newPassword);

      showSuccess('Palavra-passe atualizada com sucesso.');
      setPwdDialogOpen(false);
      setNewPassword('');
    } catch (err) {
      console.error(err);

      const msg =
        err?.code === 'auth/requires-recent-login'
          ? 'Por segurança, entra novamente para alterar a palavra-passe.'
          : err?.message || 'Falha ao atualizar a palavra-passe.';

      showError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Tens a certeza que pretendes eliminar a conta?')) return;

    setDeleting(true);

    try {
      if (userType === 'partner' && currentUser?.uid) {
        await deleteDoc(doc(db, 'empresas', currentUser.uid));
      }

      await auth.currentUser.delete();

      showSuccess('Conta eliminada com sucesso.');
      navigate('/login', { replace: true });
    } catch (err) {
      console.error(err);

      const msg =
        err?.code === 'auth/requires-recent-login'
          ? 'Sai e volta a entrar para poderes eliminar a conta.'
          : 'Falha ao eliminar a conta.';

      showError(msg);
    } finally {
      setDeleting(false);
    }
  };

  const avatarLetter =
    (fields.nome && fields.nome[0]) ||
    (currentUser?.email && currentUser.email[0]) ||
    'U';

  const roleLabel =
    userType === 'admin'
      ? 'ADMIN'
      : userType === 'partner'
      ? 'PARCEIRO'
      : userType === 'loja'
      ? 'LOJA'
      : 'CLIENTE';

  const themeLabel =
    appearanceMode === 'system'
      ? `Sistema (${systemMode === 'dark' ? 'escuro' : 'claro'})`
      : appearanceMode === 'dark'
      ? 'Escuro'
      : 'Claro';

  const infoCards = [
    {
      icon: <PersonRoundedIcon fontSize="small" />,
      label: 'Tipo de conta',
      value: roleLabel,
    },
    {
      icon: <EmailRoundedIcon fontSize="small" />,
      label: 'E-mail atual',
      value: fields.email || '—',
    },
    {
      icon:
        userType === 'partner' ? (
          <BusinessRoundedIcon fontSize="small" />
        ) : (
          <SecurityRoundedIcon fontSize="small" />
        ),
      label: 'Preferência visual',
      value: themeLabel,
    },
  ];

  const selectAppearanceMode = (mode) => {
    setAppearanceMode(mode);
    setShowAppearance(false);

    showSuccess(
      mode === 'system'
        ? 'Tema atualizado para seguir o sistema.'
        : `Tema atualizado para ${mode === 'dark' ? 'escuro' : 'claro'}.`
    );
  };

  const Section = ({ icon, title, subtitle, children }) => (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.25, md: 2.8 },
        borderRadius: '30px',
        border: `1px solid ${colors.border}`,
        bgcolor: colors.card,
        boxShadow: isDark
          ? '0 22px 58px rgba(0,0,0,0.24)'
          : '0 20px 54px rgba(15,23,42,0.07)',
      }}
    >
      <Stack direction="row" spacing={1.45} alignItems="center" sx={{ mb: 2.4 }}>
        <Avatar
          sx={{
            width: 46,
            height: 46,
            borderRadius: '17px',
            bgcolor: alpha(colors.accent, isDark ? 0.18 : 0.1),
            color: colors.accent,
          }}
        >
          {icon}
        </Avatar>

        <Box>
          <Typography variant="h6" fontWeight={950} sx={{ color: colors.text }}>
            {title}
          </Typography>

          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>

      {children}
    </Paper>
  );

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '18px',
      bgcolor: colors.cardStrong,
      '& fieldset': { borderColor: colors.border },
      '&:hover fieldset': { borderColor: alpha(colors.accent, 0.38) },
      '&.Mui-focused fieldset': { borderColor: colors.accent, borderWidth: 1 },
    },
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress sx={{ color: colors.accent }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        px: { xs: 1.25, sm: 2.5, md: 3.5 },
        py: { xs: 2, md: 3.5 },
        background: isDark
          ? 'linear-gradient(135deg, #020617 0%, #0f172a 52%, #111827 100%)'
          : 'linear-gradient(135deg, #f8fafc 0%, #fff7ed 48%, #f8fafc 100%)',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: isDark
            ? `radial-gradient(900px 460px at 10% 5%, ${alpha(colors.accent, 0.18)}, transparent 62%), radial-gradient(760px 440px at 92% 12%, ${alpha(colors.accent, 0.12)}, transparent 58%)`
            : `radial-gradient(900px 460px at 10% 5%, ${alpha(colors.accent, 0.16)}, transparent 62%), radial-gradient(760px 440px at 92% 12%, ${alpha(colors.accent, 0.1)}, transparent 58%)`,
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: isDark ? 0.16 : 0.3,
          pointerEvents: 'none',
          backgroundImage:
            'linear-gradient(rgba(148,163,184,0.16) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.16) 1px, transparent 1px)',
          backgroundSize: '44px 44px',
          maskImage: 'linear-gradient(to bottom, black, transparent 76%)',
        }}
      />

      <Box sx={{ position: 'relative', zIndex: 1, width: '100%' }}>
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            borderRadius: { xs: '28px', md: '42px' },
            border: `1px solid ${colors.border}`,
            bgcolor: colors.surface,
            backdropFilter: 'blur(22px)',
            WebkitBackdropFilter: 'blur(22px)',
            boxShadow: isDark
              ? '0 28px 90px rgba(0,0,0,0.36)'
              : '0 24px 80px rgba(15,23,42,0.10)',
            p: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              width: '100%',
              mb: 3.5,
              p: { xs: 2.5, md: 4 },
              borderRadius: '34px',
              color: 'common.white',
              boxShadow: isDark
                ? '0 24px 70px rgba(0,0,0,0.34)'
                : '0 24px 70px rgba(15,23,42,0.22)',
              background:
                'linear-gradient(90deg, #05070b 0%, #090909 55%, #8f3f00 100%)',
              '&:before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                backgroundImage: `url(${sintanetLogo})`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: {
                  xs: 'center 24px',
                  md: 'right -120px center',
                  lg: 'right -220px center',
                  xl: 'right -280px center',
                },
                backgroundSize: {
                  xs: '260px auto',
                  md: '600px auto',
                  lg: '850px auto',
                  xl: '950px auto',
                },
                opacity: 0.08,
                filter: 'drop-shadow(0 24px 50px rgba(255,106,0,0.35))',
                pointerEvents: 'none',
              },
              '&:after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background:
                  'radial-gradient(circle at 82% 28%, rgba(255,106,0,0.34) 0%, rgba(255,106,0,0.14) 26%, transparent 52%)',
                pointerEvents: 'none',
              },
            }}
          >
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={3}
              justifyContent="space-between"
              alignItems="stretch"
              sx={{ position: 'relative', zIndex: 1 }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2.2}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
              >
                <Avatar
                  src={userType === 'partner' ? sintanetLogo : undefined}
                  variant="rounded"
                  sx={{
                    width: 92,
                    height: 92,
                    borderRadius: '30px',
                    fontSize: 36,
                    fontWeight: 950,
                    color: '#ffffff',
                    bgcolor: alpha(colors.accent, 0.28),
                    border: '1px solid rgba(255,255,255,0.18)',
                    boxShadow: '0 18px 44px rgba(0,0,0,0.18)',
                    p: userType === 'partner' ? 1.3 : 0,
                    '& img': { objectFit: 'contain' },
                  }}
                >
                  {userType === 'partner' ? null : avatarLetter.toUpperCase()}
                </Avatar>

                <Box>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.4 }}>
                    <Chip
                      label={roleLabel}
                      size="small"
                      sx={{
                        borderRadius: '999px',
                        fontWeight: 900,
                        bgcolor: alpha(colors.accent, 0.24),
                        color: '#ffffff',
                        border: '1px solid rgba(255,255,255,0.18)',
                      }}
                    />

                    {userType === 'partner' && (
                      <Chip
                        label="Empresa Parceira"
                        size="small"
                        sx={{
                          borderRadius: '999px',
                          fontWeight: 900,
                          bgcolor: 'rgba(255,255,255,0.12)',
                          color: '#ffffff',
                          border: '1px solid rgba(255,255,255,0.18)',
                        }}
                      />
                    )}
                  </Stack>

                  <Typography
                    sx={{
                      fontSize: { xs: '2.15rem', sm: '2.85rem', md: '3.7rem' },
                      fontWeight: 950,
                      lineHeight: 0.98,
                      letterSpacing: '-0.07em',
                      color: '#ffffff',
                    }}
                  >
                    {fields.nome || currentUser?.email?.split('@')[0] || 'Utilizador'}
                  </Typography>

                  <Typography
                    sx={{
                      mt: 1.4,
                      color: 'rgba(255,255,255,0.72)',
                      fontSize: { xs: '0.98rem', md: '1.08rem' },
                      lineHeight: 1.75,
                    }}
                  >
                    Gere os dados da conta, informações da empresa, segurança e aparência da aplicação.
                  </Typography>
                </Box>
              </Stack>

              <Stack
                direction={{ xs: 'row', lg: 'column' }}
                spacing={1.25}
                useFlexGap
                flexWrap="wrap"
                sx={{ width: { xs: '100%', lg: 260 }, justifyContent: 'center' }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    px: 1.6,
                    py: 1.25,
                    borderRadius: '26px',
                    bgcolor: 'rgba(10,10,10,0.48)',
                    border: '1px solid rgba(255,255,255,0.18)',
                    backdropFilter: 'blur(18px)',
                    minWidth: { xs: '100%', sm: 230 },
                  }}
                >
                  <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.66)', fontWeight: 800 }}>
                    E-mail atual
                  </Typography>

                  <Typography noWrap sx={{ fontSize: '1rem', fontWeight: 950, color: '#ffffff' }}>
                    {fields.email || '—'}
                  </Typography>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    px: 1.6,
                    py: 1.25,
                    borderRadius: '26px',
                    bgcolor: 'rgba(10,10,10,0.48)',
                    border: '1px solid rgba(255,255,255,0.18)',
                    backdropFilter: 'blur(18px)',
                    minWidth: { xs: '100%', sm: 230 },
                  }}
                >
                  <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.66)', fontWeight: 800 }}>
                    Tema
                  </Typography>

                  <Typography sx={{ fontSize: '1rem', fontWeight: 950, color: '#ffffff' }}>
                    {themeLabel}
                  </Typography>
                </Paper>
              </Stack>
            </Stack>
          </Paper>

          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {infoCards.map((item) => (
              <Grid item xs={12} md={4} key={item.label}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.2,
                    height: '100%',
                    borderRadius: '26px',
                    border: `1px solid ${colors.border}`,
                    bgcolor: colors.card,
                    boxShadow: isDark
                      ? '0 18px 42px rgba(0,0,0,0.22)'
                      : '0 16px 38px rgba(15,23,42,0.06)',
                  }}
                >
                  <Stack direction="row" spacing={1.3} alignItems="center">
                    <Avatar
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: '16px',
                        bgcolor: alpha(colors.accent, isDark ? 0.18 : 0.1),
                        color: colors.accent,
                      }}
                    >
                      {item.icon}
                    </Avatar>

                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 800 }}>
                        {item.label}
                      </Typography>

                      <Typography noWrap fontWeight={950} sx={{ color: colors.text }}>
                        {item.value}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Box
            component="form"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
          >
            <Grid container spacing={2.5}>
              {userType === 'partner' && (
                <Grid item xs={12} lg={7}>
                  <Section
                    icon={<BusinessRoundedIcon />}
                    title="Dados da Empresa"
                    subtitle="Atualiza as informações públicas e fiscais da empresa parceira."
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          label="Nome da Empresa"
                          name="nome"
                          value={fields.nome}
                          onChange={handleChange}
                          fullWidth
                          sx={inputSx}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label="Morada"
                          name="morada"
                          value={fields.morada}
                          onChange={handleChange}
                          fullWidth
                          sx={inputSx}
                          InputProps={{
                            startAdornment: (
                              <LocationOnRoundedIcon sx={{ mr: 1, color: colors.muted }} />
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          label="NIF"
                          name="nif"
                          value={fields.nif}
                          onChange={handleChange}
                          fullWidth
                          sx={inputSx}
                          InputProps={{
                            startAdornment: (
                              <BadgeRoundedIcon sx={{ mr: 1, color: colors.muted }} />
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Contacto"
                          name="contacto"
                          value={fields.contacto}
                          onChange={handleChange}
                          fullWidth
                          sx={inputSx}
                          InputProps={{
                            startAdornment: (
                              <PhoneRoundedIcon sx={{ mr: 1, color: colors.muted }} />
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Section>
                </Grid>
              )}

              <Grid item xs={12} lg={userType === 'partner' ? 5 : 12}>
                <Stack spacing={2.5}>
                  <Section
                    icon={<EmailRoundedIcon />}
                    title="Conta de Acesso"
                    subtitle="Atualiza o e-mail de login e controla a segurança da conta."
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          label="E-mail"
                          name="email"
                          type="email"
                          value={fields.email}
                          onChange={handleChange}
                          fullWidth
                          required
                          error={!!fields.email && !isValidEmail(fields.email)}
                          helperText={
                            !!fields.email && !isValidEmail(fields.email)
                              ? 'E-mail inválido'
                              : ''
                          }
                          sx={inputSx}
                        />
                      </Grid>
                    </Grid>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ mt: 2 }}>
                      <Button
                        variant="outlined"
                        startIcon={<LockRoundedIcon />}
                        onClick={() => setPwdDialogOpen(true)}
                        sx={{
                          textTransform: 'none',
                          borderRadius: '999px',
                          fontWeight: 900,
                          borderColor: alpha(colors.accent, 0.4),
                          color: colors.accent,
                        }}
                      >
                        Alterar Palavra-passe
                      </Button>

                      <Button
                        variant="outlined"
                        startIcon={<PaletteRoundedIcon />}
                        onClick={() => setShowAppearance((v) => !v)}
                        sx={{
                          textTransform: 'none',
                          borderRadius: '999px',
                          fontWeight: 900,
                          borderColor: alpha(colors.accent, 0.4),
                          color: colors.accent,
                        }}
                      >
                        Aparência
                      </Button>
                    </Stack>
                  </Section>

                  <Section
                    icon={<SecurityRoundedIcon />}
                    title="Segurança e Conta"
                    subtitle="Ações sensíveis da tua conta."
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1.2}
                      alignItems={{ xs: 'stretch', sm: 'center' }}
                    >
                      <Button
                        variant="outlined"
                        startIcon={<SecurityRoundedIcon />}
                        onClick={() => setPwdDialogOpen(true)}
                        sx={{
                          textTransform: 'none',
                          borderRadius: '999px',
                          fontWeight: 900,
                          borderColor: alpha(colors.accent, 0.4),
                          color: colors.accent,
                        }}
                      >
                        Rever Segurança
                      </Button>

                      <Box sx={{ flexGrow: 1 }} />

                      {userType === 'partner' && (
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteOutlineRoundedIcon />}
                          onClick={handleDelete}
                          disabled={deleting}
                          sx={{
                            borderRadius: '999px',
                            textTransform: 'none',
                            fontWeight: 900,
                          }}
                        >
                          {deleting ? 'A eliminar…' : 'Eliminar Conta'}
                        </Button>
                      )}
                    </Stack>
                  </Section>
                </Stack>
              </Grid>
            </Grid>

            <Collapse in={showAppearance}>
              <Paper
                elevation={0}
                sx={{
                  mt: 2.5,
                  p: { xs: 2.25, md: 2.8 },
                  borderRadius: '30px',
                  border: `1px solid ${colors.border}`,
                  bgcolor: colors.card,
                  boxShadow: isDark
                    ? '0 22px 58px rgba(0,0,0,0.24)'
                    : '0 20px 54px rgba(15,23,42,0.07)',
                }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  justifyContent="space-between"
                  sx={{ mb: 2 }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 950, color: colors.text }}>
                      Aparência da App
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Escolhe como preferes visualizar a aplicação.
                    </Typography>
                  </Box>

                  <Chip
                    label={`Modo atual: ${
                      effectiveThemeMode === 'dark' ? 'Escuro' : 'Claro'
                    }`}
                    size="small"
                    variant="outlined"
                    sx={{ borderRadius: 999, fontWeight: 900 }}
                  />
                </Stack>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Button
                      fullWidth
                      variant={appearanceMode === 'light' ? 'contained' : 'outlined'}
                      startIcon={<LightModeRoundedIcon />}
                      onClick={() => selectAppearanceMode('light')}
                      sx={{
                        borderRadius: '20px',
                        py: 1.5,
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                        fontWeight: 900,
                        bgcolor: appearanceMode === 'light' ? colors.accent : undefined,
                      }}
                    >
                      Claro
                    </Button>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Button
                      fullWidth
                      variant={appearanceMode === 'dark' ? 'contained' : 'outlined'}
                      startIcon={<DarkModeRoundedIcon />}
                      onClick={() => selectAppearanceMode('dark')}
                      sx={{
                        borderRadius: '20px',
                        py: 1.5,
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                        fontWeight: 900,
                        bgcolor: appearanceMode === 'dark' ? colors.accent : undefined,
                      }}
                    >
                      Escuro
                    </Button>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Button
                      fullWidth
                      variant={appearanceMode === 'system' ? 'contained' : 'outlined'}
                      startIcon={<SettingsBrightnessRoundedIcon />}
                      onClick={() => selectAppearanceMode('system')}
                      sx={{
                        borderRadius: '20px',
                        py: 1.5,
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                        fontWeight: 900,
                        bgcolor: appearanceMode === 'system' ? colors.accent : undefined,
                      }}
                    >
                      Sistema
                    </Button>
                  </Grid>
                </Grid>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  A preferência é guardada neste dispositivo e aplicada imediatamente.
                </Typography>
              </Paper>
            </Collapse>

            <Divider sx={{ my: 3, borderColor: colors.border }} />

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.2}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <Button
                component={RouterLink}
                to={
                  userType === 'admin'
                    ? '/admin'
                    : userType === 'loja'
                    ? '/loja'
                    : userType === 'partner'
                    ? '/dashboard'
                    : '/cliente'
                }
                startIcon={<ArrowBackRoundedIcon />}
                color="inherit"
                sx={{
                  textTransform: 'none',
                  borderRadius: '999px',
                  fontWeight: 900,
                }}
              >
                Voltar
              </Button>

              <Button
                type="submit"
                variant="contained"
                disableElevation
                disabled={saving}
                startIcon={<SaveRoundedIcon />}
                sx={{
                  minWidth: 190,
                  borderRadius: '999px',
                  textTransform: 'none',
                  fontWeight: 950,
                  py: 1.15,
                  bgcolor: colors.accent,
                  boxShadow: '0 14px 35px rgba(255,106,0,0.28)',
                  '&:hover': { bgcolor: '#ff7a1a' },
                }}
              >
                {saving ? 'A guardar…' : 'Guardar Perfil'}
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Box>

      <Dialog
        open={pwdDialogOpen}
        onClose={() => setPwdDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '28px',
            bgcolor: colors.cardStrong,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 950 }}>Alterar Palavra-passe</DialogTitle>

        <DialogContent>
          <TextField
            label="Nova Palavra-passe"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth
            sx={{ mt: 1, ...inputSx }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setPwdDialogOpen(false)}
            sx={{
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 900,
            }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={handlePwdChange}
            disabled={saving || newPassword.length < 6}
            sx={{
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 900,
              bgcolor: colors.accent,
              '&:hover': { bgcolor: '#ff7a1a' },
            }}
          >
            {saving ? 'A atualizar…' : 'Atualizar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}